import { useEffect, useState } from 'react';

/**
 * Chart colors read from the live CSS custom properties (`src/index.css`)
 * instead of hardcoded hex — so a chart repaints for free when the theme
 * changes, the same way every other component does. Per *Statistiek-
 * elementen*: ink, accent, mint-deep, pine, in that order.
 */
export interface ThemeColors {
  ink: string;
  accent: string;
  mintDeep: string;
  pine: string;
  warning: string;
  info: string;
  danger: string;
}

const FALLBACK: ThemeColors = {
  ink: '#111111',
  accent: '#474747',
  mintDeep: '#0cea88',
  pine: '#223b3d',
  warning: '#8a6d00',
  info: '#1d5fd6',
  danger: '#b42318',
};

function readThemeColors(): ThemeColors {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return FALLBACK;
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback;
  return {
    ink: read('--ink', FALLBACK.ink),
    accent: read('--accent', FALLBACK.accent),
    mintDeep: read('--mint-deep', FALLBACK.mintDeep),
    pine: read('--pine', FALLBACK.pine),
    warning: read('--warning', FALLBACK.warning),
    info: read('--info', FALLBACK.info),
    danger: read('--danger', FALLBACK.danger),
  };
}

/**
 * Recomputed whenever `<html data-theme>` changes (the theme switcher in the
 * account menu) or the OS preference changes while the user follows
 * `"system"`. Chart series get four solid lines (ink, accent, mint-deep,
 * pine) then the same four again dashed, so up to eight series stay
 * distinguishable without adding more literal colors.
 */
export function useThemeColors(): ThemeColors {
  const [colors, setColors] = useState<ThemeColors>(readThemeColors);

  useEffect(() => {
    const recompute = () => setColors(readThemeColors());
    recompute();

    const observer = new MutationObserver(recompute);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    // Not every test environment implements `matchMedia` (jsdom does not by
    // default) — the `data-theme` observer above already covers an explicit
    // light/dark choice, this only adds "system" following a live OS change.
    const media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-color-scheme: dark)')
        : null;
    media?.addEventListener('change', recompute);

    return () => {
      observer.disconnect();
      media?.removeEventListener('change', recompute);
    };
  }, []);

  return colors;
}

/**
 * A fixed color per well-known metric key, so the same metric always reads
 * the same across charts (e.g. `stress` is always the warning color) and a
 * single-series chart doesn't default to plain `ink` — every metric this
 * page currently plots (sleep/stress/hrv/steps) gets its own hue instead of
 * all four collapsing onto the same line color.
 */
const METRIC_COLOR: Record<string, keyof ThemeColors> = {
  sleep_duration: 'pine',
  stress: 'warning',
  hrv: 'info',
  steps: 'mintDeep',
  heart_rate: 'danger',
};

/** Chart series stroke order: four solid colors, then the same four dashed —
 * used as a fallback for a metric key not in `METRIC_COLOR`. */
export function seriesStroke(
  colors: ThemeColors,
  index: number,
  metricKey?: string,
): { stroke: string; dashed: boolean } {
  const metricColor = metricKey ? METRIC_COLOR[metricKey] : undefined;
  if (metricColor) {
    return { stroke: colors[metricColor], dashed: false };
  }
  const palette = [colors.ink, colors.accent, colors.mintDeep, colors.pine];
  const color = palette[index % palette.length] ?? colors.ink;
  const dashed = Math.floor(index / palette.length) % 2 === 1;
  return { stroke: color, dashed };
}
